"use client";

/**
 * MultiCombobox — searchable multi-select dropdown.
 *
 * Sibling to <Combobox>; share the same dropdown chrome but the trigger
 * shows selected items as chips and the option list uses checkboxes.
 *
 * Use when:
 *   - The set of selectable items is medium/large (≥ 10) AND
 *   - Multiple selections are valid (e.g. "notify these team members")
 *
 * Behavior:
 *   - Trigger renders a chip per selected option; an "× All" chip clears
 *     the selection. Click a chip's "×" to remove just that one.
 *   - Click anywhere on the trigger (other than a chip's ×) to open.
 *   - Dropdown: search input on top + checkbox list. Click a row toggles
 *     it in or out of the selection without closing.
 *   - Esc / outside click / Tab on search input closes the dropdown.
 *   - "Clear all" button at the dropdown bottom when ≥ 2 selected.
 *   - `topAction` pins a single row above the list (e.g. "+ Create Field"),
 *     mirroring <Combobox>. Enter commits it when the filtered list is empty.
 */

import {
  forwardRef,
  KeyboardEvent,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { ChevronDown, Search, X } from "lucide-react";
import { cn } from "@/lib/utils/cn";

export interface MultiComboboxOption {
  value: string;
  label: string;
  disabled?: boolean;
  description?: string;
  /**
   * Optional avatar URL. When present, the option row and the selected
   * chip render a small circular avatar before the label. When absent,
   * a fallback initials circle is rendered using the first letter of
   * `label`. Use for member/user pickers.
   */
  avatarUrl?: string;
  /**
   * Optional group label. When ANY option has a non-empty `group`, the
   * dropdown renders a small uppercase section header before each group.
   * Options without `group` go in an unlabeled first section. `selectAllVisible`
   * still operates across the entire filtered list, not per-group.
   */
  group?: string;
}

export interface MultiComboboxProps {
  value: string[];
  onChange: (value: string[]) => void;
  options: MultiComboboxOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  loading?: boolean;
  disabled?: boolean;
  /** `true` for red border only, or a string to display below. */
  error?: boolean | string;
  label?: string;
  /** Optional cap on visible chips before collapsing to "+N more". */
  maxChips?: number;
  /**
   * Hard cap on how many options can be selected. At the cap the unselected
   * rows go disabled and "Select all" only fills the remaining room, so the
   * picker can never build a selection the server will reject. Values already
   * over the cap are left alone: the control never silently drops a selection.
   */
  maxSelected?: number;
  className?: string;
  id?: string;
  size?: "default" | "sm";
  filter?: (option: MultiComboboxOption, query: string) => boolean;
  /**
   * Called whenever the search text changes. Provide this to drive
   * server-side search: the consumer debounces the query, refetches, and
   * passes the results back as `options` (pair with `filter={() => true}` to
   * disable the built-in client-side filter).
   */
  onSearchChange?: (query: string) => void;
  /**
   * When true, render a header row above the option list that selects or
   * clears all currently visible (filtered, non-disabled) options at once.
   * Tri-state visual: empty / dash / checked.
   */
  selectAllVisible?: boolean;
  /**
   * Trigger appearance.
   * - `default`: standard chip-rendering trigger, sized by `size`.
   * - `pill`: label-only trigger for filter bars. Geometry mirrors <FilterSelect>
   *   so both sit on the same row without drifting; `size` is ignored.
   */
  variant?: "default" | "pill";
  /**
   * Overrides the pill trigger text. Receives the resolved selected options, so
   * a label built from selection survives a server-side search that drops those
   * options from `options`. Ignored by the `default` variant.
   */
  triggerLabel?: (selected: MultiComboboxOption[]) => string;
  /** Optional pinned row at the top of the list (e.g. "+ Create Field"). */
  topAction?: {
    label: ReactNode;
    onSelect: () => void;
  };
  "aria-label"?: string;
  "data-testid"?: string;
}

const triggerBase =
  "flex items-center justify-between gap-2 rounded-lg border border-border bg-background text-foreground transition-colors focus-within:ring-2 focus-within:ring-ring disabled:pointer-events-none disabled:opacity-50";

const triggerSizes = {
  default: "min-h-10 px-2 py-1 text-sm",
  sm: "min-h-8 px-2 py-1 text-xs",
} as const;

const pillTrigger =
  "h-10 rounded-md border-input px-3 text-sm hover:bg-accent max-w-[260px]";

export const MultiCombobox = forwardRef<HTMLDivElement, MultiComboboxProps>(
  (
    {
      value,
      onChange,
      options,
      placeholder = "Select...",
      searchPlaceholder = "Search...",
      emptyMessage = "No matching results found",
      loading = false,
      disabled = false,
      error,
      label,
      maxChips = 5,
      maxSelected,
      className,
      id,
      size = "default",
      filter,
      onSearchChange,
      selectAllVisible = false,
      variant = "default",
      triggerLabel,
      topAction,
      "aria-label": ariaLabel,
      "data-testid": testId,
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");

    const generatedId = useId();
    const triggerId = id ?? `multi-combobox-${generatedId}`;
    const listboxId = `${triggerId}-listbox`;

    const containerRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const optionsByValue = useMemo(() => {
      const m = new Map<string, MultiComboboxOption>();
      options.forEach((o) => m.set(o.value, o));
      return m;
    }, [options]);

    // Remember option data for every value that has appeared in `options`, so a
    // selected chip keeps its label even after a server-side search removes that
    // option from the current result set. Harmless for client-side lists (where
    // `options` always holds the full set).
    const seenOptionsRef = useRef(new Map<string, MultiComboboxOption>());
    useEffect(() => {
      options.forEach((o) => seenOptionsRef.current.set(o.value, o));
    }, [options]);

    const selectedOptions = useMemo(
      () =>
        value
          .map((v) => optionsByValue.get(v) ?? seenOptionsRef.current.get(v))
          .filter((o): o is MultiComboboxOption => Boolean(o)),
      [value, optionsByValue],
    );

    const filteredOptions = useMemo(() => {
      if (!search.trim()) return options;
      const lower = search.trim().toLowerCase();
      const fn =
        filter ??
        ((o: MultiComboboxOption, q: string) =>
          o.label.toLowerCase().includes(q));
      return options.filter((o) => fn(o, lower));
    }, [options, search, filter]);

    // Grouping is active when ANY option declares a non-empty `group`. We
    // preserve the original options order; ungrouped options form an
    // unlabeled first section. Filtered groups with zero matches are hidden.
    const isGrouped = useMemo(
      () => options.some((o) => o.group && o.group.trim().length > 0),
      [options],
    );
    const renderSections = useMemo(() => {
      if (!isGrouped) return null;
      const sections: { label: string | null; options: MultiComboboxOption[] }[] = [];
      const byLabel = new Map<string | null, number>();
      filteredOptions.forEach((opt) => {
        const key = opt.group && opt.group.trim().length > 0 ? opt.group : null;
        let sectionIdx = byLabel.get(key);
        if (sectionIdx === undefined) {
          sectionIdx = sections.length;
          byLabel.set(key, sectionIdx);
          sections.push({ label: key, options: [] });
        }
        sections[sectionIdx].options.push(opt);
      });
      return sections;
    }, [isGrouped, filteredOptions]);

    // Notify consumers of search changes so they can drive server-side search.
    // Fires on typing and on every reset-to-"" path (close/escape/tab), so a
    // server-backed list refetches back to its unfiltered state on close.
    useEffect(() => {
      onSearchChange?.(search);
    }, [search, onSearchChange]);

    // Close on outside click.
    useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(e.target as Node)
        ) {
          setIsOpen(false);
          setSearch("");
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Esc to close.
    useEffect(() => {
      if (!isOpen) return;
      const onKey = (e: globalThis.KeyboardEvent) => {
        if (e.key === "Escape") {
          setIsOpen(false);
          setSearch("");
        }
      };
      document.addEventListener("keydown", onKey);
      return () => document.removeEventListener("keydown", onKey);
    }, [isOpen]);

    // Auto-focus search on open.
    useEffect(() => {
      if (isOpen) {
        requestAnimationFrame(() => searchInputRef.current?.focus());
      }
    }, [isOpen]);

    const atCapacity = maxSelected !== undefined && value.length >= maxSelected;

    const toggleValue = useCallback(
      (val: string) => {
        if (value.includes(val)) {
          onChange(value.filter((v) => v !== val));
          return;
        }
        if (maxSelected !== undefined && value.length >= maxSelected) return;
        onChange([...value, val]);
      },
      [value, onChange, maxSelected],
    );

    const removeChip = useCallback(
      (val: string, e: React.MouseEvent) => {
        e.stopPropagation();
        onChange(value.filter((v) => v !== val));
      },
      [value, onChange],
    );

    const clearAll = useCallback(() => {
      onChange([]);
    }, [onChange]);

    const handleTriggerClick = () => {
      if (disabled || loading) return;
      setIsOpen((p) => {
        if (!p) setSearch("");
        return !p;
      });
    };

    const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Tab") {
        setIsOpen(false);
        setSearch("");
      } else if (e.key === "Enter" && topAction && filteredOptions.length === 0) {
        e.preventDefault();
        topAction.onSelect();
        setIsOpen(false);
        setSearch("");
      }
    };

    const visibleChips = selectedOptions.slice(0, maxChips);
    const overflowCount = selectedOptions.length - visibleChips.length;
    const hasError = Boolean(error);
    const errorMessage = typeof error === "string" && error ? error : null;
    const isPill = variant === "pill";

    // Select-all over the currently filtered + enabled set.
    const selectableFiltered = useMemo(
      () => filteredOptions.filter((o) => !o.disabled),
      [filteredOptions],
    );
    const selectedSet = useMemo(() => new Set(value), [value]);
    const filteredSelectedCount = useMemo(
      () => selectableFiltered.filter((o) => selectedSet.has(o.value)).length,
      [selectableFiltered, selectedSet],
    );
    const allFilteredSelected =
      selectableFiltered.length > 0 &&
      filteredSelectedCount === selectableFiltered.length;
    const someFilteredSelected =
      filteredSelectedCount > 0 && !allFilteredSelected;

    const renderOption = (opt: MultiComboboxOption) => {
      const isSelected = value.includes(opt.value);
      // At the cap only the already-selected rows stay live, so the user can swap
      // one out for another without first clearing the whole selection.
      const isBlocked = Boolean(opt.disabled) || (atCapacity && !isSelected);
      const hasAvatarField = opt.avatarUrl !== undefined;
      return (
        <button
          key={opt.value}
          type="button"
          role="option"
          aria-selected={isSelected}
          aria-disabled={isBlocked}
          disabled={isBlocked}
          onMouseDown={(e) => {
            e.preventDefault();
            if (!isBlocked) toggleValue(opt.value);
          }}
          className={cn(
            "flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors",
            isBlocked
              ? "cursor-not-allowed text-muted-foreground opacity-50"
              : "cursor-pointer text-popover-foreground hover:bg-accent",
            isSelected && "bg-accent/40",
          )}
        >
          <span
            aria-hidden="true"
            className={cn(
              "flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors",
              isSelected
                ? "border-primary bg-primary text-primary-foreground"
                : "border-input bg-background",
            )}
          >
            {isSelected && (
              <svg
                viewBox="0 0 20 20"
                fill="currentColor"
                className="h-3 w-3"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </span>
          {hasAvatarField &&
            (opt.avatarUrl ? (
              <img
                src={opt.avatarUrl}
                alt=""
                aria-hidden="true"
                className="h-5 w-5 shrink-0 rounded-full object-cover"
              />
            ) : (
              <span
                aria-hidden="true"
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-medium uppercase text-muted-foreground"
              >
                {opt.label.trim().charAt(0) || "?"}
              </span>
            ))}
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate">{opt.label}</span>
            {opt.description && (
              <span className="truncate text-xs text-muted-foreground">
                {opt.description}
              </span>
            )}
          </span>
        </button>
      );
    };

    const handleSelectAllVisible = useCallback(() => {
      if (selectableFiltered.length === 0) return;
      if (allFilteredSelected) {
        // Deselect only the visible ones; keep any selections outside the filter.
        const visibleValues = new Set(selectableFiltered.map((o) => o.value));
        onChange(value.filter((v) => !visibleValues.has(v)));
      } else {
        // Add any visible-but-unselected to the existing selection, stopping at the
        // cap: "Select all" on a workspace with more options than the cap allows was
        // the fastest way to build a selection the server rejects on save.
        const next = new Set(value);
        for (const o of selectableFiltered) {
          if (maxSelected !== undefined && next.size >= maxSelected) break;
          next.add(o.value);
        }
        onChange(Array.from(next));
      }
    }, [allFilteredSelected, selectableFiltered, value, onChange, maxSelected]);

    return (
      <div
        ref={ref}
        className={cn("relative", isPill ? "w-auto" : "w-full", className)}
        data-testid={testId}
      >
        {label && (
          <label
            htmlFor={triggerId}
            className="mb-1.5 block text-sm font-medium text-foreground"
          >
            {label}
          </label>
        )}
        <div ref={containerRef} className="relative">
          <button
            id={triggerId}
            type="button"
            disabled={disabled || loading}
            onClick={handleTriggerClick}
            className={cn(
              triggerBase,
              isPill ? pillTrigger : triggerSizes[size],
              "pr-9 text-left",
              isPill ? "w-auto min-w-[140px]" : "w-full",
              hasError && "border-danger focus-within:ring-danger",
              (disabled || loading) && "cursor-not-allowed",
            )}
            role="combobox"
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-controls={listboxId}
            aria-label={ariaLabel}
            aria-invalid={hasError ? true : undefined}
          >
            {isPill ? (
              <span className="truncate text-foreground">
                {loading
                  ? "Loading..."
                  : triggerLabel
                    ? triggerLabel(selectedOptions)
                    : selectedOptions.length === 0
                      ? placeholder
                      : `${placeholder} (${selectedOptions.length})`}
              </span>
            ) : selectedOptions.length === 0 ? (
              <span className="text-muted-foreground">
                {loading ? "Loading..." : placeholder}
              </span>
            ) : (
              <span className="flex flex-wrap items-center gap-1">
                {visibleChips.map((opt) => (
                  <span
                    key={opt.value}
                    className="inline-flex items-center gap-1 rounded-md bg-accent px-2 py-0.5 text-xs text-accent-foreground"
                  >
                    {opt.avatarUrl !== undefined &&
                      (opt.avatarUrl ? (
                        <img
                          src={opt.avatarUrl}
                          alt=""
                          aria-hidden="true"
                          className="h-4 w-4 shrink-0 rounded-full object-cover"
                        />
                      ) : (
                        <span
                          aria-hidden="true"
                          className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-background/60 text-[9px] font-medium uppercase"
                        >
                          {opt.label.trim().charAt(0) || "?"}
                        </span>
                      ))}
                    <span className="max-w-[12rem] truncate">{opt.label}</span>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => removeChip(opt.value, e)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          onChange(value.filter((v) => v !== opt.value));
                        }
                      }}
                      aria-label={`Remove ${opt.label}`}
                      className="rounded-sm hover:bg-background/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    >
                      <X aria-hidden="true" className="h-3 w-3" />
                    </span>
                  </span>
                ))}
                {overflowCount > 0 && (
                  <span className="rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                    +{overflowCount} more
                  </span>
                )}
              </span>
            )}
          </button>
          <ChevronDown
            aria-hidden="true"
            className={cn(
              "pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground transition-transform duration-150",
              label && "top-[calc(50%+12px)]",
              isOpen && "rotate-180",
            )}
          />
          {isOpen && (
            <div className="absolute z-50 mt-1 w-full min-w-[260px] overflow-hidden rounded-lg border border-border bg-popover shadow-md">
              <div className="border-b border-border p-2">
                <div className="relative">
                  <Search
                    aria-hidden="true"
                    className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    placeholder={searchPlaceholder}
                    className="w-full rounded-md border border-border bg-background pl-8 pr-2 py-1.5 text-sm text-foreground outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
                    aria-controls={listboxId}
                  />
                </div>
              </div>
              <div
                id={listboxId}
                role="listbox"
                aria-multiselectable="true"
                className="max-h-60 overflow-y-auto py-1"
              >
                {topAction && (
                  <button
                    type="button"
                    className="flex w-full items-center border-b border-border px-3 py-2 text-left text-sm font-medium text-primary transition-colors hover:bg-accent"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      topAction.onSelect();
                      setIsOpen(false);
                      setSearch("");
                    }}
                  >
                    {topAction.label}
                  </button>
                )}
                {selectAllVisible && selectableFiltered.length > 0 && (
                  <button
                    type="button"
                    // At the cap the row can only mean "select more", which is exactly
                    // what is not allowed — clearing runs through "Clear all" instead.
                    disabled={atCapacity && !allFilteredSelected}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      if (atCapacity && !allFilteredSelected) return;
                      handleSelectAllVisible();
                    }}
                    className={cn(
                      "flex w-full items-center gap-2 border-b border-border px-3 py-2 text-left text-sm font-medium transition-colors",
                      atCapacity && !allFilteredSelected
                        ? "cursor-not-allowed text-muted-foreground opacity-50"
                        : "text-popover-foreground hover:bg-accent",
                    )}
                    aria-label={
                      allFilteredSelected ? "Deselect all" : "Select all"
                    }
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        "flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors",
                        allFilteredSelected || someFilteredSelected
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-input bg-background",
                      )}
                    >
                      {allFilteredSelected ? (
                        <svg
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          className="h-3 w-3"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : someFilteredSelected ? (
                        <span className="block h-0.5 w-2 rounded-sm bg-current" />
                      ) : null}
                    </span>
                    <span className="flex-1 truncate">
                      {allFilteredSelected ? "Deselect all" : "Select all"}
                    </span>
                  </button>
                )}
                {filteredOptions.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-muted-foreground">
                    {emptyMessage}
                  </div>
                ) : isGrouped && renderSections ? (
                  renderSections.map((section, sectionIdx) => (
                    <div key={section.label ?? `__ungrouped__${sectionIdx}`}>
                      {section.label && (
                        <div
                          className={cn(
                            "mt-2 mb-1 px-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground",
                            sectionIdx > 0 && "border-t border-border pt-2",
                          )}
                        >
                          {section.label}
                        </div>
                      )}
                      {section.options.map((opt) => renderOption(opt))}
                    </div>
                  ))
                ) : (
                  filteredOptions.map((opt) => renderOption(opt))
                )}
              </div>
              {value.length > 0 && (
                <div className="border-t border-border bg-muted/30 px-2 py-1.5 flex items-center justify-between text-xs">
                  <span
                    className={cn(
                      "text-muted-foreground",
                      atCapacity && maxSelected !== undefined && "text-foreground",
                    )}
                  >
                    {maxSelected === undefined
                      ? `${value.length} selected`
                      : `${value.length} of ${maxSelected} selected`}
                  </span>
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      clearAll();
                    }}
                    className="text-primary hover:underline focus-visible:underline focus-visible:outline-none"
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        {errorMessage && (
          <p className="mt-1.5 text-xs text-danger">{errorMessage}</p>
        )}
      </div>
    );
  },
);

MultiCombobox.displayName = "MultiCombobox";
